import apiClient from './apiClient'
import type {
  CenterKind,
  CenterMessage,
  Notification,
  NotificationCenterParams,
  NotificationCounts,
  NotificationDetail,
  NotificationListParams,
  NotificationResponse,
} from '@/types/notification'

/**
 * Fetch user notifications
 * @param userId - ID of the user to fetch notifications for
 * @param perPage - Number of notifications to fetch (default: 100)
 */
export async function fetchUserNotifications(
  userId: number,
  perPage: number = 100,
): Promise<NotificationResponse> {
  const response = await apiClient.get<NotificationResponse>('/api/user_notification', {
    params: {
      userId,
      perPage,
    },
  })

  return response.data
}

/** Fetch one profile Messages folder (matches Angular NotificationsComponent). */
export async function fetchProfileNotifications({
  userId,
  folder,
  page = 1,
  perPage = 15,
  q,
}: NotificationListParams): Promise<NotificationResponse> {
  const folderParams =
    folder === 'all'
      ? {}
      : folder === 'inbox'
        ? { unread: 1 }
        : { [folder]: 1 }

  const response = await apiClient.get<NotificationResponse | Notification[]>(
    '/api/user_notification',
    {
      params: {
        userId,
        ...folderParams,
        page,
        perPage,
        ...(q?.trim() ? { q: q.trim() } : {}),
      },
    },
  )
  const payload = response.data
  if (Array.isArray(payload)) {
    return { current: 1, total: payload.length, last: 1, data: payload }
  }
  return {
    current: payload?.current ?? 1,
    total: payload?.total ?? 0,
    last: payload?.last ?? 1,
    data: Array.isArray(payload?.data) ? payload.data : [],
  }
}

/** API `unread` is mentions only — always add Updates + System for folder badges. */
export function combineProfileNotificationCounts(
  raw: Partial<NotificationCounts> | null | undefined,
): NotificationCounts {
  const mentionUnread = Number(raw?.unread ?? 0)
  const mentionRead = Number(raw?.read ?? 0)
  const mentionInbox = Number(raw?.inbox ?? mentionUnread + mentionRead)
  const mentionDeleted = Number(raw?.deleted ?? 0)
  const whatsNew = Number(raw?.whats_new ?? 0)
  const whatsNewUnread = Number(raw?.whats_new_unread ?? 0)
  const whatsNewDeleted = Number(raw?.whats_new_deleted ?? 0)
  const system = Number(raw?.system_announcement ?? 0)
  const systemUnread = Number(raw?.system_announcement_unread ?? 0)
  const systemDeleted = Number(raw?.system_announcement_deleted ?? 0)

  return {
    inbox: mentionInbox + whatsNew + system,
    unread: mentionUnread + whatsNewUnread + systemUnread,
    starred: Number(raw?.starred ?? 0),
    read: mentionRead,
    deleted: mentionDeleted + whatsNewDeleted + systemDeleted,
    mentions: mentionInbox,
    mentions_unread: mentionUnread,
    whats_new: whatsNew,
    whats_new_unread: whatsNewUnread,
    system_announcement: system,
    system_announcement_unread: systemUnread,
  }
}

export async function fetchProfileNotificationCounts(
  userId: number,
): Promise<NotificationCounts> {
  const response = await apiClient.get<Partial<NotificationCounts>>(
    '/api/user_notification_count',
    { params: { userId } },
  )
  return combineProfileNotificationCounts(response.data)
}

/** Unified All / Unread feed: user mentions + What's New + System Announcements. */
export async function fetchNotificationCenter({
  folder,
  source = 'all',
  page = 1,
  perPage = 15,
  q,
  allowAllKind = true,
}: NotificationCenterParams): Promise<NotificationResponse & { data: CenterMessage[] }> {
  const response = await apiClient.get<NotificationResponse & { data: CenterMessage[] }>(
    '/api/notification_center',
    {
      params: {
        folder:
          folder === 'inbox' ? 'unread' : folder === 'deleted' ? 'deleted' : folder,
        source,
        page,
        perPage,
        allowAllKind: allowAllKind ? 1 : 0,
        ...(q?.trim() ? { q: q.trim() } : {}),
      },
    },
  )
  const payload = response.data
  return {
    current: payload?.current ?? 1,
    total: payload?.total ?? 0,
    last: payload?.last ?? 1,
    data: Array.isArray(payload?.data) ? payload.data : [],
  }
}

export async function updateProfileNotification(
  notificationId: number,
  payload: Record<string, number>,
): Promise<void> {
  await apiClient.post(`/api/user_notification/${notificationId}`, payload)
}

export async function deleteProfileNotifications(
  userId: number,
  notificationIds: number[],
): Promise<void> {
  await apiClient.post(`/api/user_notification/multi_delete/${userId}`, {
    notification_ids: notificationIds,
    deleted: 1,
  })
}

export async function deleteCenterNotifications(
  items: Array<{ kind: CenterKind; id: number }>,
): Promise<void> {
  if (items.length === 0) return
  await apiClient.post('/api/notification_center/delete', { items })
}

export async function restoreCenterNotifications(
  items: Array<{ kind: CenterKind; id: number }>,
): Promise<void> {
  if (items.length === 0) return
  await apiClient.post('/api/notification_center/restore', { items })
}

/**
 * Fetch a single notification detail (matches Angular loadNotificationDetail).
 */
export async function fetchUserNotificationDetail(
  notificationId: number,
): Promise<NotificationDetail> {
  const response = await apiClient.get<NotificationDetail | { data: NotificationDetail }>(
    `/api/user_notification/${notificationId}`,
  )
  const payload = response.data
  if (payload && typeof payload === 'object' && 'data' in payload && payload.data) {
    const inner = payload.data
    if (
      typeof inner === 'object' &&
      ('notes' in inner ||
        'notes_for' in inner ||
        'notification_id' in inner ||
        'subject' in inner ||
        'fk_ref_id' in inner)
    ) {
      return inner
    }
  }
  return payload as NotificationDetail
}

/**
 * Mark all user notifications as read (matches Angular markAllRead).
 */
export async function markAllNotificationsRead(): Promise<void> {
  await apiClient.post('/api/user_notification_read_all')
}
